/**
 * Hook for handling file export operations
 */

import { useState, useCallback } from 'react'
import type { ExportOptions, IncomeStatement } from '../types/financials.types'
import { formatCurrency } from '../utils/formatting'

export const useFileExport = () => {
	const [isExporting, setIsExporting] = useState(false)
	const [exportError, setExportError] = useState<Error | null>(null)

	/**
	 * Export income statement to Excel file
	 */
	const exportFile = useCallback(
		async (incomeStatement: IncomeStatement, options: ExportOptions) => {
			setIsExporting(true)
			setExportError(null)

			try {
				if (options.format === 'xlsx') {
					await exportToExcel(incomeStatement, options)
				} else if (options.format === 'csv') {
					await exportToCSV(incomeStatement, options)
				}

				setIsExporting(false)
			} catch (error) {
				const err = error instanceof Error ? error : new Error('Failed to export file')
				setExportError(err)
				setIsExporting(false)
				throw err
			}
		},
		[],
	)

	return {
		isExporting,
		exportError,
		exportFile,
	}
}

/**
 * Export to Excel using xlsx library
 */
const exportToExcel = async (incomeStatement: IncomeStatement, options: ExportOptions) => {
	try {
		// Dynamically import xlsx to reduce bundle size
		const XLSX = await import('xlsx')

		// Filter quarters based on options (most recent first)
		const quarters = incomeStatement.quarters
			.filter(q => options.quarters.includes(q.id))
			.sort((a, b) => b.order - a.order)

		// Filter line items
		const lineItems = incomeStatement.lineItems
			.filter(li => {
				if (!options.includeCalculatedFields && li.type === 'calculated') {
					return false
				}
				return true
			})
			.sort((a, b) => a.order - b.order)

		// Create worksheet data
		const data: any[][] = []

		// Header row
		const headerRow = ['Line Item', ...quarters.map(q => q.label)]
		data.push(headerRow)

		// Data rows
		lineItems.forEach(lineItem => {
			const row: (string | number)[] = [lineItem.label]

			quarters.forEach(quarter => {
				const value = lineItem.values.find(v => v.quarter === quarter.id)?.value || 0
				row.push(value)
			})

			data.push(row)
		})

		// Create workbook and worksheet
		const ws = XLSX.utils.aoa_to_sheet(data)
		const wb = XLSX.utils.book_new()
		XLSX.utils.book_append_sheet(wb, ws, 'Income Statement')

		// Apply formatting
		const range = XLSX.utils.decode_range(ws['!ref'] || 'A1')

		// Format header row (bold)
		for (let C = range.s.c; C <= range.e.c; ++C) {
			const address = XLSX.utils.encode_cell({ r: 0, c: C })
			if (!ws[address]) continue
			ws[address].s = {
				font: { bold: true },
				fill: { fgColor: { rgb: 'F3F4F6' } },
			}
		}

		// Format number columns as currency
		for (let R = range.s.r + 1; R <= range.e.r; ++R) {
			for (let C = range.s.c + 1; C <= range.e.c; ++C) {
				const address = XLSX.utils.encode_cell({ r: R, c: C })
				if (!ws[address]) continue
				ws[address].z = '$#,##0'
			}
		}

		// Set column widths
		ws['!cols'] = [{ wch: 30 }, ...quarters.map(() => ({ wch: 12 }))]

		// Generate filename
		const storeName = incomeStatement.location || 'Store'
		const date = new Date().toISOString().split('T')[0]
		const filename = `${storeName}_Income_Statement_${date}.xlsx`

		// Write and download
		XLSX.writeFile(wb, filename)
	} catch (error) {
		throw new Error(`Excel export failed: ${error}`)
	}
}

/**
 * Export to CSV
 */
const exportToCSV = async (incomeStatement: IncomeStatement, options: ExportOptions) => {
	// Filter quarters (most recent first)
	const quarters = incomeStatement.quarters
		.filter(q => options.quarters.includes(q.id))
		.sort((a, b) => b.order - a.order)

	// Filter line items
	const lineItems = incomeStatement.lineItems
		.filter(li => {
			if (!options.includeCalculatedFields && li.type === 'calculated') {
				return false
			}
			return true
		})
		.sort((a, b) => a.order - b.order)

	// Build CSV string
	let csv = ''

	// Header row
	csv += 'Line Item,' + quarters.map(q => `"${q.label}"`).join(',') + '\n'

	// Data rows
	lineItems.forEach(lineItem => {
		const row = [
			`"${lineItem.label}"`,
			...quarters.map(quarter => {
				const value = lineItem.values.find(v => v.quarter === quarter.id)?.value || 0
				return value
			}),
		]
		csv += row.join(',') + '\n'
	})

	// Create blob and download
	const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
	const url = URL.createObjectURL(blob)
	const link = document.createElement('a')
	link.setAttribute('href', url)

	const storeName = incomeStatement.location || 'Store'
	const date = new Date().toISOString().split('T')[0]
	link.setAttribute('download', `${storeName}_Income_Statement_${date}.csv`)

	link.style.visibility = 'hidden'
	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)
}

export type UseFileExportReturn = ReturnType<typeof useFileExport>
